// client/src/components/common/TabsComponent.tsx
import React from 'react';
import { Box, Collapse, SxProps, Tab, Tabs, Typography } from '@mui/material';
import { useResponsive } from '../../contexts/ResponsiveContext';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { ReactComponent as DropDown } from '../../static/tab_image/tabDropDown.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface TabsComponentProps {
    url: number;
    setOpenMenu: React.Dispatch<React.SetStateAction<boolean>>;
    openMenu: boolean;
    handleChange: (event: React.SyntheticEvent | null, newUrl: number) => void;
    menuItems: { label: string; value: number }[];
    tabTypography: SxProps;
    isMenuExpandMoreIcon?: boolean;
}

export const GetResponsiveWidth = () => {
    const { isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();

    return isScreenXl
        ? '1120px'
        : isScreenLg
          ? '885px'
          : isScreenMd
            ? '650px'
            : isScreenSm
              ? '422px'
              : isScreenXs
                ? '320px'
                : '1185px';
};

const TabsComponent: React.FC<TabsComponentProps> = ({
    url,
    setOpenMenu,
    openMenu,
    handleChange,
    menuItems,
    tabTypography,
    isMenuExpandMoreIcon,
}) => {
    const { isScreenLg, isScreenMd, isScreenSm, isScreenXs, responsiveWidth } = useResponsive();
    const currentLabel = menuItems.find((item) => item.value === url)?.label || '';

    return (
        <>
            <Box
                sx={{
                    zIndex: 1,
                    width: '100%',
                    backgroundColor: isScreenSm || isScreenXs ? 'white' : '#7A7C81',
                }}
                data-testid={'tabs_component'}
            >
                <Box
                    sx={{
                        zIndex: 2,
                        backgroundColor: '#7A7C81',
                        width: responsiveWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                    }}
                    data-testid={'tabs_component_inner_box'}
                >
                    {isScreenSm || isScreenXs ? (
                        <>
                            <Box
                                onClick={() => setOpenMenu(!openMenu)}
                                sx={{
                                    width: isScreenSm ? '422px' : isScreenXs ? '320px' : '422px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    zIndex: 1,
                                    px: 0,
                                    py: 1,
                                }}
                            >
                                <Typography sx={{ ...tabTypography, color: 'white' }}>
                                    {currentLabel}
                                </Typography>
                                {isMenuExpandMoreIcon ? (
                                    openMenu ? (
                                        <ExpandLessIcon sx={{ color: 'white' }} />
                                    ) : (
                                        <ExpandMoreIcon sx={{ color: 'white' }} />
                                    )
                                ) : (
                                    <DropDown />
                                )}
                            </Box>
                            <Collapse in={openMenu} timeout="auto" unmountOnExit>
                                <Box
                                    sx={{
                                        backgroundColor: 'white',
                                        width: isScreenSm
                                            ? '422px'
                                            : isScreenXs
                                              ? '320px'
                                              : '422px',
                                    }}
                                >
                                    {menuItems.map((item) => (
                                        <Box
                                            key={item.value}
                                            sx={{
                                                px: 0,
                                                py: 1,
                                                borderBottom: '1px solid #000',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                handleChange(null, item.value);
                                                setOpenMenu(false);
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    ...tabTypography,
                                                    color: '#B75332',
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Collapse>
                        </>
                    ) : (
                        <Tabs
                            sx={{}}
                            TabIndicatorProps={{ hidden: true }}
                            value={url}
                            onChange={handleChange}
                            aria-label="product tabs"
                        >
                            {menuItems.map((item, index) => (
                                <Tab
                                    key={item.value}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <div
                                                className={'iconHover'}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        ...tabTypography,
                                                        marginLeft: isScreenLg
                                                            ? 0
                                                            : isScreenMd || isScreenSm
                                                              ? -1
                                                              : 2,
                                                        marginRight: isScreenLg
                                                            ? 0
                                                            : isScreenMd || isScreenSm
                                                              ? -1
                                                              : 2,
                                                        fontSize: 18,
                                                        fontFamily: 'Poppins',
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                            </div>
                                        </Box>
                                    }
                                    {...a11yProps(index)}
                                    sx={
                                        index === 0
                                            ? styles.firstProductTabsStyle('white')
                                            : styles.productTabsStyle('white')
                                    }
                                />
                            ))}
                        </Tabs>
                    )}
                </Box>
            </Box>
        </>
    );
};

function a11yProps(index: number) {
    return {
        id: `product-tab-${index}`,
        'aria-controls': `product-tabpanel-${index}`,
    };
}

export default TabsComponent;
